import { useEffect, useState } from 'react';
import { Button, Modal, Form, Alert } from 'react-bootstrap';

import { getFileMatches, getDuplicatesByHash } from '../utils/Database';
import { setRequestAdded } from '../utils/Requests';
import { populateListsFromView } from "../utils/Views";
import { clearFlag, deleteDatabaseRecord, restoreDatabaseRecord } from '../utils/Database';
import { showToast } from '../utils/Toasts';

import useUserStore from "../stores/User";

import { Typeahead } from "react-bootstrap-typeahead";
import { LoadingRing } from './LoadingRing';
import { LoadingText } from './LoadingText';

export function ConfirmationModal({
    show,
    title = null,
    message,
    onConfirm,
    onHide,
    linkDescription = null,
    linkUrl = null,
    okButton = "OK",
    cancelButton = "Cancel"
}) {
    const handleConfirm = async () => {
        if (onConfirm) {
            const result = onConfirm();
            if (typeof result === 'function') {
                await result();
            }
        }
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
        >
            {title && (
                <Modal.Header closeButton>
                    <Modal.Title>{title}</Modal.Title>
                </Modal.Header>
            )}
            <Modal.Body style={{ whiteSpace: "pre-line" }}>
                {message}
                {linkDescription && linkUrl && (
                    <>
                        <br/><br/>
                        <a href={linkUrl} target="_blank" rel="noopener noreferrer">{linkDescription}</a>
                    </>
                )}  
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    {cancelButton}
                </Button>
                <Button variant="primary" onClick={handleConfirm}>
                    {okButton}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export function AddConfirmationModal({ id, show, title, writers, onConfirm, onHide }) {
    const [scriptMatches, setScriptMatches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [matchedScriptId, setMatchedScriptId] = useState(null);

    const handleConfirm = async () => {
        const success = await setRequestAdded(id, matchedScriptId);
        setMatchedScriptId(null);
        
        if (onConfirm && success) {
            const result = onConfirm();
            if (typeof result === 'function') {
                await result();
            }
        }
    };

    useEffect(() => {
        const fetchMatches = async () => {
            try {
                setLoading(true);
                const matches = await getFileMatches(title, writers);                
                if (matches) {
                    setScriptMatches(matches);
                }
            } catch (error) {
                console.error("Error fetching matches:", error);
            } finally {
                setLoading(false);
            }
        };

        if (show) {
            setScriptMatches([]);
            fetchMatches();
        }
    }, [show, title, writers]);

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
        >
            <Modal.Header>
                <Modal.Title>Mark Added</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loading ? (<LoadingRing />) : (
                    scriptMatches.length > 0 ? (
                        <>
                            <p>
                                Select the matching script in the database:
                            </p>
                            <Form className="mb-3">
                                {scriptMatches.map((match, index) => (
                                    <Form.Check
                                        key={index}
                                        type="radio"
                                        name="scriptMatch"
                                        id={`scriptMatch-${index}`}
                                    >
                                        <Form.Check.Input 
                                            type="radio" 
                                            name="scriptMatch"
                                            value={match.file_id}
                                            onChange={(e) => {
                                                setMatchedScriptId(e.target.value);
                                            }}
                                            checked={matchedScriptId === match.file_id}
                                        />
                                        <Form.Check.Label htmlFor={`scriptMatch-${index}`}>
                                            <small>
                                                <a href={`/database/script/${match.file_id}`} target="_blank" rel="noopener noreferrer">
                                                    {match.script_title || ""}
                                                </a>
                                                {match.writers ? ` by ${match.writers}` : ''},{match.additional_info_concat ? ` ${match.additional_info_concat},` : ''} <em>{match.date_added ? `added ${match.date_added}` : ''}</em>
                                            </small>
                                        </Form.Check.Label>
                                    </Form.Check>
                                ))}
                            </Form>
                        </>
                    ) : (
                        <p>
                            {`Are you sure you want to mark ${title}${writers ? ` by ${writers}` : ''} as added?`}<br/><br/>
                            {"There aren't any matches in the database."}
                        </p>
                    )
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleConfirm}>
                    Confirm
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export function DeleteConfirmationModal({ show, fileInfo, onConfirm, onHide }) {
    const userData = useUserStore((state) => state.data);
    const [userID, setUserID] = useState(userData ? userData.user_id : null);

    const [lists, setLists] = useState<{ [key: string]: string[] }>({});
    const [formData, setFormData] = useState({
        flag_reason: fileInfo?.flag_reason ? fileInfo?.flag_reason : '',
    });

    const title = fileInfo && fileInfo["Script Title"] ? fileInfo["Script Title"] : "";
    const writers = fileInfo && fileInfo["Writer(s)"] ? fileInfo["Writer(s)"] : "";

    const handleConfirm = async () => {
        const flagReason = String(formData.flag_reason ?? '')
            .replace(/,/g, '')
            .trim();
        await deleteDatabaseRecord(fileInfo?.file_id, userID, flagReason);
        if (onConfirm) {
            const result = onConfirm();
            if (typeof result === 'function') {
                await result();
            }
        }
    };

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setFormData(prev => ({
            ...prev,
            [name]: typeof value === 'string' ? value : value['label']
        }));

    };

    useEffect(() => {
        const fetchLists = async () => {
            const listsData = await populateListsFromView(['flag_reason']);
            setLists(listsData);
        };

        fetchLists();
    }, []);

    useEffect(() => {
        setFormData({
            flag_reason: fileInfo?.flag_reason ? fileInfo?.flag_reason : '',
        });
    }, [fileInfo]);

    useEffect(() => {
        if (userData) {
            setUserID(userData.user_id);
        }
    }, [userData]);

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
        >
            <Modal.Body>
                <p>
                    {`Are you sure you want to delete ${title}${writers ? ` by ${writers}` : ''}?`}<br /><br />
                    {"The record will be hidden from all non-admin users and can be recovered if necessary."}
                </p>
                <h6 className="mb-2 fw-bold">Flag Reason (optional)</h6>
                <Typeahead
                    allowNew={true}
                    flip={true}
                    maxHeight="200px"
                    inputProps={{ className: "mt-0" }}
                    placeholder="Set a flag reason..."
                    selected={formData.flag_reason ? [formData.flag_reason] : []}
                    options={lists.flag_reason ?? []}
                    onChange={(e) => {
                        e.length > 0 ?
                            handleInputChange({ target: { name: 'flag_reason', value: e[0] } }) :
                            handleInputChange({ target: { name: 'flag_reason', value: '' } });
                    }
                    }
                    onBlur={(e) => {
                        const inputValue = e.target.value;
                        if (inputValue && (!formData.flag_reason || formData.flag_reason !== inputValue)) {
                            handleInputChange({ target: { name: 'flag_reason', value: inputValue } });
                        }
                    }}
                />
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleConfirm}>
                    Delete Script
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export function RestoreConfirmationModal({ show, fileInfo, onConfirm, onHide }) {
    const userData = useUserStore((state) => state.data);
    const [userID, setUserID] = useState(userData ? userData.user_id : null);
    const [clearFlag, setClearFlag] = useState(false);

    const title = fileInfo && fileInfo["Script Title"] ? fileInfo["Script Title"] : "";
    const writers = fileInfo && fileInfo["Writer(s)"] ? fileInfo["Writer(s)"] : "";

    const handleConfirm = async () => {
        await restoreDatabaseRecord(fileInfo?.file_id, userID, clearFlag);
        setClearFlag(false);
        
        if (onConfirm) {
            const result = onConfirm();
            if (typeof result === 'function') {
                await result();
            }
        }
    };

    useEffect(() => {
        if (userData) {
            setUserID(userData.user_id);
        }
    }, [userData]);

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
        >
            <Modal.Body>
                <p>
                    {`Are you sure you want to restore ${title}${writers ? ` by ${writers}` : ''}?`}<br /><br />
                    {"The record will be visible to all users again."}
                </p>
                {fileInfo && fileInfo?.flag_reason && (
                    <>
                        <h6 className="mb-2 fw-bold">Flag Reason</h6>
                        <p className="text-danger">{fileInfo?.flag_reason}</p>
                        <Form.Check
                            className="mt-3"
                            type="checkbox"
                            label="Clear flag reason?"
                            checked={clearFlag}
                            onChange={(e) => setClearFlag(e.target.checked)}
                        />
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleConfirm}>
                    Restore Script
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export function RequestMatchConfirmationModal({ matches, show, onConfirm, onHide }) {
    const [matchedScriptId, setMatchedScriptId] = useState(null);

    const handleConfirm = async (matchedValue) => {
        if (onConfirm) {
            const result = onConfirm(matchedValue);
            if (typeof result === 'function') {
                await result();
            }
        }
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
        >
            <Modal.Header>
                <Modal.Title>Mark Added</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p>
                    This script matches one or more Requests.<br/><br/>Please either select one to Mark Added or Skip to leave requests unchanged.
                </p>
                <Form className="mb-3">
                    {matches.map((match, index) => {
                        const dateRequested = match.date_requested ? new Date(match.date_requested).toLocaleDateString() : null;
                        return (
                            <Form.Check
                                key={index}
                                type="radio"
                                name="requestMatch"
                                id={`requestMatch-${index}`}
                            >
                                <Form.Check.Input
                                    type="radio"
                                    name="requestMatch"
                                    value={match.id}
                                    onChange={(e) => {
                                        setMatchedScriptId(e.target.value);
                                    }}
                                />
                                <Form.Check.Label htmlFor={`requestMatch-${index}`}>
                                    <small>
                                        {match.script_title || ""}{match.writers ? ` by ${match.writers}` : ''}
                                        {match.additional_info ? `, ${match.additional_info}` : ''}<br/>
                                        <em>{dateRequested ? `Requested ${dateRequested}` : ''}</em>
                                    </small>
                                </Form.Check.Label>
                            </Form.Check>
                        );
                    })}
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Skip
                </Button>
                <Button
                    variant="primary"
                    onClick={() => {
                        handleConfirm(matchedScriptId);
                    }}
                    disabled={!matchedScriptId}
                >
                    Mark Added
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export function DeleteAccountModal({ show, onConfirm, onHide }) {
    const [confirmText, setConfirmText] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);
    const isValid = confirmText === 'DELETE';

    const handleConfirm = async () => {
        if (!isValid) return;
        setIsDeleting(true);
        await onConfirm();
        setIsDeleting(false);
    };

    const handleHide = () => {
        setConfirmText('');
        onHide();
    };

    return (
        <Modal show={show} onHide={handleHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>Delete Account</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Alert variant="danger">
                    <strong>This action is permanent and cannot be undone.</strong> Your account and all associated data will be permanently deleted.
                </Alert>
                <p>To confirm, type <strong>DELETE</strong> in the box below:</p>
                <Form.Control
                    type="text"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="Type DELETE to confirm"
                    autoComplete="off"
                />
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleHide} disabled={isDeleting}>
                    Cancel
                </Button>
                <Button variant="danger" onClick={handleConfirm} disabled={!isValid || isDeleting}>
                    {isDeleting ? 'Deleting...' : 'Delete Account'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export function RemoveDuplicatesModal({ show, fileInfo, onConfirm, onHide }) {
    const userData = useUserStore((state) => state.data);
    const [userID, setUserID] = useState(userData ? userData.user_id : null);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [keptScriptID, setKeptScriptID] = useState(null);
    const [duplicateScripts, setDuplicateScripts] = useState([]);

    const handleConfirm = async (keepThisID) => {
        const flagCleared = await clearFlag(keepThisID, userID);

        if (!flagCleared) {
            showToast("error", "Unable to clear flag from selected record. Cancelling deduplication.");
            return;
        }

        for (const duplicate of duplicateScripts) {
            if (duplicate.file_id !== keepThisID) {
                await deleteDatabaseRecord(duplicate.file_id, userID, "Duplicate - Removed", false);
            }
        }

        if (onConfirm) {
            const result = onConfirm();
            if (typeof result === 'function') {
                await result();
            }
        }

        setProcessing(false);
    };

    useEffect(() => {
        const fetchDuplicates = async () => {
            try {
                setLoading(true);
                const duplicates = await getDuplicatesByHash("google_drive_hash", fileInfo?.google_drive_hash);
                if (duplicates) {
                    setDuplicateScripts(duplicates);
                } else {
                    const duplicatesByTitle = await getFileMatches(fileInfo["Script Title"], fileInfo["Writer(s)"]);
                    setDuplicateScripts(duplicatesByTitle);
                }
            } catch (error) {
                console.error("Error fetching duplicates:", error);
            } finally {
                setLoading(false);
            }
        };

        if (show && fileInfo) {
            setDuplicateScripts([]);
            fetchDuplicates();
        }
    }, [show, fileInfo]);

    useEffect(() => {
        if (userData) {
            setUserID(userData.user_id);
        }
    }, [userData]);

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
        >
            <Modal.Header>
                <Modal.Title>Remove Duplicates</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: "60vh", overflowY: "auto" }}>
                <p>
                    This script is currently flagged as a duplicate.<br /><br />Please select one record to keep. All others will be marked as deleted and hidden.
                </p>
                {loading ? <LoadingRing/> : (
                    <Form className="mb-3">
                        {duplicateScripts.map((duplicate, index) => {
                            const scriptInfo = [
                                duplicate["Writer(s)"] ? `by ${duplicate["Writer(s)"]}` : null,
                                duplicate["Date Added"] ? `added ${duplicate["Date Added"]}` : null,
                                duplicate["Draft Date"] ? `draft date ${duplicate["Draft Date"]}` : null,
                                duplicate["additional_info_concat"] ? duplicate["additional_info_concat"] : null,
                                duplicate.tags?.length ? duplicate.tags.join(", ") : null
                            ].filter(Boolean).join(", ");

                            return (
                                <Form.Check
                                    key={index}
                                    type="radio"
                                    name="duplicates"
                                    id={`duplicates-${index}`}
                                >
                                    <Form.Check.Input
                                        type="radio"
                                        name="duplicates"
                                        value={duplicate.file_id}
                                        onChange={(e) => {
                                            setKeptScriptID(e.target.value);
                                        }}
                                    />
                                    <Form.Check.Label htmlFor={`duplicates-${index}`}>
                                        <small>
                                            {duplicate["Script Title"] && (
                                                <p className="mb-2">
                                                    <a href={`/database/script/${duplicate.file_id}`} target="_blank" rel="noopener noreferrer">
                                                        {duplicate["Script Title"]}
                                                    </a>
                                                    {duplicate.file_id === fileInfo?.file_id && (
                                                        <>
                                                            <br />
                                                            <span className="text-success">Currently selected record</span>
                                                        </>
                                                    )}
                                                    <br/>
                                                    <span className="text-danger">
                                                        {(duplicate.google_drive_hash === fileInfo?.google_drive_hash) || (duplicate.storage_hash === fileInfo?.storage_hash) ? (
                                                                "Exact duplicate - same file hash"
                                                        ) : (
                                                                "Similarity match only, may not be exact duplicate"
                                                        )}
                                                    </span>
                                                    <br />
                                                    <em>{scriptInfo}</em>
                                                </p>
                                            )}
                                        </small>
                                    </Form.Check.Label>
                                </Form.Check>
                            );
                        })}
                    </Form>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={() => {
                        setProcessing(true);
                        handleConfirm(keptScriptID);
                    }}
                    disabled={!keptScriptID}
                >
                    {processing ? (
                        <LoadingText text="Removing..." className="mb-0" />
                    ) : (
                        "Remove Duplicates"
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}